import React from 'react';

import styled from '@emotion/styled';

import { BREAKPOINTS, disabledColor, unit, white } from '@zego/theme';
import { chunkArray } from '@zego/utils';

import H1 from '@zego/components/atoms/H1';
import RegularText from '@zego/components/atoms/RegularText';
import View from '@zego/components/atoms/View';
import ContentContainer from '@zego/components/ContentContainer';
import SectionDisplay from '@zego/components/SectionDisplay';
import SmallHeaderText from '@zego/components/SmallHeaderText';

import Choice from '@zego/static/svgs/Choice.svg';
import CustomerService from '@zego/static/svgs/CustomerService.svg';
import HiddenFees from '@zego/static/svgs/HiddenFees.svg';
import Hourly from '@zego/static/svgs/Hourly.svg';
import NewProducts from '@zego/static/svgs/NewProducts.svg';
import Paper from '@zego/static/svgs/Paper.svg';

import Backdrop from './Backdrop.svg';

const Icon = styled('img')({
  width: 61,
  height: 61,
  display: 'block',
});

const ItemRow = styled(View)({
  display: 'flex',
  flexDirection: 'row',
});

const ItemContainer = styled(View)({
  marginBottom: unit(8),
  flex: 1,
  '&:first-of-type': {
    marginRight: unit(4),
  },
  [BREAKPOINTS.medium]: {
    flex: 1,
    '&:first-of-type': {
      marginRight: unit(11),
    },
  },
});

const IconContainer = styled(View)({
  marginBottom: unit(3),
});

const HeaderText = styled(SmallHeaderText)({
  marginBottom: unit(2),
});

const DescriptionText = styled(RegularText)({
  fontSize: 16,
  color: disabledColor(7),
  marginBottom: unit(2),
});

const SubTitle = styled(RegularText)({
  display: 'none',
  [BREAKPOINTS.medium]: {
    display: 'block',
    marginTop: unit(6),
    fontSize: 20,
    lineHeight: '26px',
    color: disabledColor(7),
  },
});

const WhyZego = () => {
  const REASONS = [
    {
      title: 'Pago por Uso',
      description:
        'Optimiza tus costes: Solo pagarás por el uso real de tu vehículo.',
      icon: Paper,
    },
    {
      title: 'Estamos cerca de ti',
      description:
        'Llama o chatea con nuestro equipo de atención al cliente, ellos se encargarán de resolver todas tus dudas.',
      icon: CustomerService,
    },
    {
      title: 'Somos flexibles',
      description:
        'Creamos pólizas que se adaptan a ti. Te cubriremos cuando más lo necesitas.',
      icon: Choice,
    },
    {
      title: 'Somos transparentes',
      description:
        'Sin gastos ocultos. No te cobramos por cambios en la póliza. Las circunstancias cambian y nosotros nos adaptamos.',
      icon: HiddenFees,
    },
    {
      title: 'Disponibilidad total',
      description:
        'Estamos disponibles 24/7 a través de la app, consulta tu cuenta cuando quieras donde quieras.',
      icon: Hourly,
    },
    {
      title: 'Nuevos productos',
      description:
        'Tenemos cada vez más productos, pero si tienes alguna recomendación te escucharemos encantados.',
      icon: NewProducts,
    },
  ];

  return (
    <SectionDisplay
      css={{
        backgroundColor: disabledColor(2),
        paddingBottom: 0,
        [BREAKPOINTS.contentContainer]: {
          paddingBottom: 0,
        },
      }}
    >
      <div
        id="why-zego"
        css={{
          backgroundColor: white,
          backgroundPosition: 'right 0px top 0px',
          [BREAKPOINTS.contentContainer]: {
            backgroundSize: '110%',
            backgroundImage: `url(${Backdrop})`,
            backgroundRepeat: 'no-repeat',
            paddingBottom: unit(5),
            [BREAKPOINTS.contentContainer]: {
              paddingBottom: unit(10),
            },
          },
        }}
      >
        <ContentContainer
          css={{
            paddingTop: unit(4),
            paddingBottom: unit(4),
            [BREAKPOINTS.medium]: {
              display: 'flex',
              flexDirection: 'row',
            },
            [BREAKPOINTS.contentContainer]: {
              paddingTop: unit(16),
              paddingBottom: 0,
            },
          }}
        >
          <View
            css={{
              [BREAKPOINTS.medium]: {
                flex: 1,
                marginRight: unit(11),
                marginBottom: 0,
              },
            }}
          >
            <H1>¿Por qué Zego?</H1>
            <SubTitle>
              Porque te ofrecemos un seguro a medida que se adapta a tus
              necesidades. Descubre nuestras ventajas.
            </SubTitle>
          </View>

          <View
            css={{
              paddingTop: unit(8),
              [BREAKPOINTS.medium]: { flex: 1.5, paddingTop: 0 },
            }}
          >
            {chunkArray(REASONS, 2).map((row, index) => (
              <ItemRow key={index}>
                {row.map((reason, rowIndex) => (
                  <ItemContainer key={rowIndex}>
                    <IconContainer>
                      <Icon src={reason.icon} />
                    </IconContainer>
                    <HeaderText>{reason.title}</HeaderText>
                    <DescriptionText>{reason.description}</DescriptionText>
                  </ItemContainer>
                ))}
              </ItemRow>
            ))}
          </View>
        </ContentContainer>
      </div>
    </SectionDisplay>
  );
};

export default WhyZego;
